import { useEffect, useState, type ReactNode } from 'react'
import { Link, useParams } from 'react-router-dom'
import { AlertTriangle, Bot, ChevronLeft, Contact, Crown, Flag, Gavel, Gift, History, LifeBuoy, MessagesSquare, MonitorSmartphone, ShieldAlert, StickyNote, Users, Wallet } from 'lucide-react'
import { useT } from '@/i18n'
import { PageHeader } from '@/shell/AppShell'
import { fetchUser, type AdminUserDetail } from '@/lib/adapters'
import { PERMISSIONS } from '@/lib/permissions'
import { InfoRow, MonoValue, PIIField, RelativeTime } from '@/components/DataDisplay'
import { StatusBadge, Tag } from '@/components/StatusBadge'
import { ErrorState, PermissionDenied, Skeleton } from '@/components/States'
import { useAuth } from '@/providers/AuthProvider'

/**
 * A3.02 — Foydalanuvchi kartasi. GET /admin/users/:id (backend `99ccb51`).
 * PII (telefon) maskalangan, ochish auditga tushishi kerak (hozir klient-side).
 */
export function UserCard() {
  const t = useT()
  const { can } = useAuth()
  const { id = '' } = useParams()
  const [user, setUser] = useState<AdminUserDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [reload, setReload] = useState(0)

  useEffect(() => {
    const ac = new AbortController()
    let alive = true
    setLoading(true)
    setError(null)
    fetchUser(Number(id), ac.signal)
      .then((u) => {
        if (alive) setUser(u)
      })
      .catch((e: unknown) => {
        if (!alive || (e instanceof DOMException && e.name === 'AbortError')) return
        setError(e instanceof Error ? e : new Error(String(e)))
      })
      .finally(() => {
        if (alive) setLoading(false)
      })
    return () => {
      alive = false
      ac.abort()
    }
  }, [id, reload])

  if (!can(PERMISSIONS.USERS_VIEW)) return <PermissionDenied permission={PERMISSIONS.USERS_VIEW} />

  const name = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—'
    : ''

  const actions = (
    <div className="flex items-center gap-1.5">
      <Link
        to={`/users/${id}/contact`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Contact size={13} />
        {t.users.contact}
      </Link>
      <Link
        to={`/users/${id}/sessions`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <MonitorSmartphone size={13} />
        {t.users.sessions}
      </Link>
      <Link
        to={`/users/${id}/login-history`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <History size={13} />
        Kirish tarixi
      </Link>
      <Link
        to={`/users/${id}/groups`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <MessagesSquare size={13} />
        {t.users.groups}
      </Link>
      <Link
        to={`/users/${id}/owned-groups`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Crown size={13} />
        {t.users.ownedGroups}
      </Link>
      <Link
        to={`/users/${id}/wallet`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Wallet size={13} />
        {t.users.wallet}
      </Link>
      <Link
        to={`/users/${id}/gifts`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Gift size={13} />
        {t.userExtra.giftsTitle}
      </Link>
      <Link
        to={`/users/${id}/strikes`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Gavel size={13} />
        {t.strikes.title}
      </Link>
      <Link
        to={`/users/${id}/notes`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <StickyNote size={13} />
        Izohlar
      </Link>
      <Link
        to={`/users/${id}/risk`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <ShieldAlert size={13} />
        Risk
      </Link>
      <Link
        to={`/users/${id}/warnings`}
        className="inline-flex items-center gap-1.5 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 text-sm text-amber-300 hover:border-amber-500/60 hover:text-amber-200"
      >
        <AlertTriangle size={13} />
        Ogohlantirish
      </Link>
      <Link
        to={`/users/${id}/tickets`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <LifeBuoy size={13} />
        {t.userExtra.ticketsTitle}
      </Link>
      <Link
        to={`/users/${id}/reports`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Flag size={13} />
        {t.userExtra.reportsTitle}
      </Link>
      <Link
        to={`/users/${id}/related`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Users size={13} />
        {t.userExtra.relatedTitle}
      </Link>
      <Link
        to={`/users/${id}/bots`}
        className="inline-flex items-center gap-1.5 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:border-border-strong hover:text-primary"
      >
        <Bot size={13} />
        {t.userExtra.botsTitle}
      </Link>
      <Link
        to="/users"
        className="inline-flex items-center gap-1 rounded-md border border-border-default bg-surface-2 px-2.5 py-1 text-sm text-secondary hover:text-primary"
      >
        <ChevronLeft size={13} />
        {t.nav.users}
      </Link>
    </div>
  )

  return (
    <>
      <PageHeader
        title={user ? name || (user.username ? `@${user.username}` : `#${user.id}`) : t.users.cardTitle}
        screenId="A3.07"
        actions={actions}
      />
      <div className="min-h-0 flex-1 overflow-auto p-3">
        {loading ? (
          <div className="max-w-2xl space-y-2 rounded-lg border border-border-default bg-surface-2 p-4">
            {Array.from({ length: 8 }, (_, i) => (
              <Skeleton key={i} className="h-4 w-2/3" />
            ))}
          </div>
        ) : error ? (
          <ErrorState code={error.message} onRetry={() => setReload((n) => n + 1)} />
        ) : !user ? (
          <ErrorState title={t.states.notFoundTitle} />
        ) : (
          <div className="max-w-2xl rounded-lg border border-border-default bg-surface-2 p-4">
            <div className="mb-3 flex items-center gap-2">
              <StatusBadge status={user.status === 'banned' ? 'banned' : 'active'} />
              {user.is_bot && <Tag>bot</Tag>}
              {user.is_official && <Tag>official</Tag>}
            </div>
            <Field label={t.col.id}>
              <MonoValue value={String(user.id)} />
            </Field>
            <Field label={t.users.userNumber}>
              {user.user_number ? <MonoValue value={user.user_number} /> : '—'}
            </Field>
            <Field label={t.col.name}>{name}</Field>
            <Field label={t.col.username}>{user.username ? `@${user.username}` : '—'}</Field>
            <Field label={t.col.phone}>
              {user.phone ? <PIIField value={user.phone} level="L2" /> : '—'}
            </Field>
            <Field label={t.users.email}>{user.email || '—'}</Field>
            <Field label={t.col.country}>{user.country_code || '—'}</Field>
            <Field label={t.users.birthday}>{user.birthday || '—'}</Field>
            <Field label={t.users.bio}>{user.bio || '—'}</Field>
            {user.is_banned && (
              <Field label={t.users.bannedReason}>
                <span className="text-danger">{user.banned_reason || '—'}</span>
              </Field>
            )}
            <Field label={t.col.registered}>
              <RelativeTime value={user.created_at} />
            </Field>
            <Field label={t.users.lastSeen}>
              {user.last_seen ? <RelativeTime value={user.last_seen} /> : '—'}
            </Field>
          </div>
        )}
      </div>
    </>
  )
}

function Field({ label, children }: { label: string; children: ReactNode }) {
  return <InfoRow label={label}>{children}</InfoRow>
}
